import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import PageHeader from '../components/ui/PageHeader';
import Pagination from '../components/ui/Pagination';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton } from '../components/ui/States';
import FeedbackFilters from '../components/feedback/FeedbackFilters';
import FeedbackTabs from '../components/feedback/FeedbackTabs';
import FeedbackTable from '../components/feedback/FeedbackTable';
import TicketPanel from '../components/feedback/TicketPanel';
import useFeedback from '../components/feedback/useFeedback';
import { toRowViews } from '../components/feedback/feedbackAdapter';
import { PAGE_SIZES } from '../components/feedback/feedbackParams';

const WIDE_QUERY = '(min-width: 1280px)';

/** True on xl screens, where the panel sits next to the table instead of in a drawer. */
function useIsWide() {
  return useSyncExternalStore(
    (onChange) => {
      if (!window.matchMedia) return () => {};
      const mql = window.matchMedia(WIDE_QUERY);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () => Boolean(window.matchMedia?.(WIDE_QUERY).matches),
  );
}

function errorView(error) {
  if (error?.status === 403) return { icon: 'lock', title: 'Admin access required', retry: false };
  if (error?.status === 429) return { icon: 'hourglass_top', title: 'Too many requests, try again shortly', retry: true, retryAfter: 5 };
  return { icon: 'cloud_off', title: "Couldn't load feedback", description: error?.message, retry: true };
}

const fmt = (n) => n.toLocaleString('en-US');

/** Customer Feedback: list with filters and tabs, plus the ticket / reply panel for ?id. */
export default function FeedbackPage() {
  const queryClient = useQueryClient();
  const wide = useIsWide();
  const {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearFilters,
    rows,
    counts,
    pagination,
    isInitialLoading,
    isRefreshing,
    isFetching,
    error,
    refetch,
  } = useFeedback();

  const views = useMemo(() => toRowViews(rows, params), [rows, params]);

  // Unsent reply text: confirm before closing the panel or switching tickets
  const dirty = useRef(false);
  const onDirtyChange = useCallback((value) => {
    dirty.current = value;
  }, []);
  const [pendingId, setPendingId] = useState(undefined); // undefined = nothing pending, null = close

  const goTo = (id) => {
    dirty.current = false;
    setParams({ id });
  };
  const requestTicket = (id) => {
    if (id === params.id) return;
    if (dirty.current && params.id) setPendingId(id);
    else goTo(id);
  };

  const total = pagination?.totalItems ?? 0;
  const from = total ? (params.page - 1) * params.limit + 1 : 0;
  const to = Math.min(params.page * params.limit, total);
  const hasFilters = Boolean(params.search || params.status !== 'all' || params.category !== 'all' || params.source !== 'all' || params.from || params.days !== '30');

  let body;
  if (error && !isInitialLoading) {
    const view = errorView(error);
    body = (
      <ErrorState
        icon={view.icon}
        title={view.title}
        description={view.description}
        retryAfter={view.retryAfter}
        onRetry={view.retry ? () => refetch() : undefined}
      />
    );
  } else if (isInitialLoading) {
    body = <TableSkeleton rows={5} message="Loading feedback…" />;
  } else if (pagination && views.length === 0) {
    body = (
      <EmptyState
        tone="neutral"
        icon="forum"
        title="No feedback matches these filters"
        description={hasFilters ? 'Try another tab, date range or search.' : 'New feedback from the app shows up here.'}
        action={
          hasFilters && (
            <Button variant="secondary" icon="filter_alt_off" onClick={clearFilters}>
              Clear filters
            </Button>
          )
        }
      />
    );
  } else {
    body = <FeedbackTable rows={views} selectedId={params.id} refreshing={isRefreshing} onOpen={requestTicket} />;
  }

  const panelOpen = Boolean(params.id);

  return (
    <>
      <PageHeader
        title="Customer Feedback"
        description="Review incoming app feedback, fuel price discrepancies, bug reports, and send email replies to users."
      />

      <div className={`grid grid-cols-1 gap-6 items-start ${panelOpen && wide ? 'xl:grid-cols-12' : ''}`}>
        <div className={`flex flex-col gap-4 min-w-0 ${panelOpen && wide ? 'xl:col-span-8' : ''}`}>
          <FeedbackFilters
            params={params}
            setParams={setParams}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            refreshing={isFetching}
            onRefresh={() => {
              refetch();
              if (params.id) queryClient.invalidateQueries({ queryKey: ['feedback', params.id] });
            }}
          />

          <FeedbackTabs status={params.status} category={params.category} counts={counts} onSelect={(changes) => setParams(changes)} />

          <section className="relative bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs overflow-hidden">
            <p className="sr-only" role="status" aria-live="polite">
              {isInitialLoading || isRefreshing || !pagination ? '' : `${total} ${total === 1 ? 'feedback' : 'feedbacks'} found`}
            </p>
            {isRefreshing && (
              <div className="absolute top-2 right-3 z-20 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-surface-container-lowest/90 shadow-xs text-[11px] text-on-surface-variant">
                <Icon name="progress_activity" size={14} className="animate-spin text-secondary" />
                Loading…
              </div>
            )}
            {body}
            {!error && pagination && total > 0 && (
              <Pagination
                page={params.page}
                pageSize={params.limit}
                total={total}
                onPageChange={(page) => setParams({ page })}
                pageSizeOptions={PAGE_SIZES}
                onPageSizeChange={(limit) => setParams({ limit })}
                disabled={isRefreshing}
                summary={
                  <span>
                    Showing <strong className="font-data-mono text-on-surface">{fmt(from)}</strong> to{' '}
                    <strong className="font-data-mono text-on-surface">{fmt(to)}</strong> of{' '}
                    <strong className="font-data-mono text-on-surface">{fmt(total)}</strong> {total === 1 ? 'feedback' : 'feedbacks'}
                  </span>
                }
              />
            )}
          </section>
        </div>

        {panelOpen && wide && (
          <div className="xl:col-span-4 min-w-0 self-stretch">
            <TicketPanel key={params.id} id={params.id} wide onRequestClose={() => requestTicket(null)} onDirtyChange={onDirtyChange} />
          </div>
        )}
      </div>

      {panelOpen && !wide && (
        <TicketPanel key={params.id} id={params.id} wide={false} onRequestClose={() => requestTicket(null)} onDirtyChange={onDirtyChange} />
      )}

      <ConfirmDialog
        open={pendingId !== undefined}
        title="Discard your unsent reply?"
        description="The text and attachments you added to this reply will be lost."
        confirmLabel="Discard reply"
        cancelLabel="Keep writing"
        onConfirm={() => {
          const next = pendingId;
          setPendingId(undefined);
          goTo(next);
        }}
        onCancel={() => setPendingId(undefined)}
      />
    </>
  );
}
